import { Text } from 'fabric'
import { TEXT_OBJECT_PADDING, TEXT_OBJECT_BACKGROUND_BORDER_RADIUS } from '../../constants'

/**
 * Добавляет скругленный прямоугольник к текущему path (без beginPath/closePath)
 * Используется для создания составного path из нескольких прямоугольников
 * @param ctx - контекст canvas
 * @param x - координата X левого верхнего угла
 * @param y - координата Y левого верхнего угла
 * @param width - ширина прямоугольника
 * @param height - высота прямоугольника
 * @param radius - радиус скругления углов
 */
function addRoundedRectToPath(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
): void {
    ctx.moveTo(x + radius, y)
    ctx.lineTo(x + width - radius, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
    ctx.lineTo(x + width, y + height - radius)
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
    ctx.lineTo(x + radius, y + height)
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
    ctx.lineTo(x, y + radius)
    ctx.quadraticCurveTo(x, y, x + radius, y)
    ctx.closePath()
}

/**
 * Добавляет обычный прямоугольник к текущему path (без beginPath)
 * Используется для создания составного path из нескольких прямоугольников
 */
function addRectToPath(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number
): void {
    ctx.moveTo(x, y)
    ctx.lineTo(x + width, y)
    ctx.lineTo(x + width, y + height)
    ctx.lineTo(x, y + height)
    ctx.closePath()
}

/**
 * Кастомный класс Text с поддержкой padding и border radius для подложки
 * Переопределяет метод _renderBackground для визуального отображения подложки
 * с отступами и скругленными углами
 */
export class CustomText extends Text {
    /**
     * Отступ подложки (px)
     * Если не задан, используется значение из константы
     */
    declare textBackgroundPadding?: number

    /**
     * Скругление углов подложки (px)
     * Если не задан, используется значение из константы
     */
    declare textBackgroundBorderRadius?: number

    /**
     * Вычисляет координаты X и ширину для каждой строки текста (как это делает Fabric.js)
     * Используется для точного позиционирования подложки
     * ВАЖНО: Используем те же значения ширины строк, что и Fabric.js (__lineWidths)
     * @returns объект с массивами координат X и ширин строк
     */
    private _calculateLineRenderX(ctx: CanvasRenderingContext2D): { x: number[], widths: number[] } {
        const lineRenderX: number[] = []
        const lineWidthsCalculated: number[] = []
        
        // Получаем информацию о строках
        const textLines = this._getTextLinesArray(this.text)
        const textWidth = this.width ?? 0
        
        // Получаем ширину строк из кэша Fabric.js (если доступна)
        // @ts-ignore - доступ к внутренним свойствам Fabric.js
        const lineWidths: number[] | undefined = this.__lineWidths
        
        // Вычисляем координаты X для каждой строки (как это делает Fabric.js)
        for (let i = 0; i < textLines.length; i++) {
            const line = textLines[i]
            
            // Вычисляем ширину строки напрямую
            ctx.font = `${this.fontSize ?? 12}px ${this.fontFamily ?? 'Arial'}`
            let lineWidth = ctx.measureText(line).width
            // Учитываем charSpacing
            const charSpacing = this.charSpacing ?? 0
            if (charSpacing !== 0 && line.length > 1) {
                lineWidth += charSpacing * (line.length - 1)
            }
            
            // ВАЖНО: Используем значение из __lineWidths, если оно доступно и больше вычисленного
            // Fabric.js может учитывать дополнительные факторы при вычислении ширины (например, особенности шрифта)
            // Используем максимум из двух значений, чтобы подложка точно покрывала весь текст
            if (lineWidths && lineWidths[i] !== undefined) {
                lineWidth = Math.max(lineWidth, lineWidths[i])
            }
            
            lineWidthsCalculated[i] = lineWidth
            
            // Получаем lineLeftOffset от Fabric.js (смещение левого края строки относительно левого края bounding box)
            // Используем метод Fabric.js для точного значения
            let lineLeftOffset: number = 0
            try {
                // @ts-ignore - используем внутренний метод Fabric.js
                lineLeftOffset = this._getLineLeftOffset(i)
            } catch {
                // Если метод недоступен, рассчитываем вручную
                if (line && line.trim() !== '') {
                    if (this.textAlign === 'left') {
                        lineLeftOffset = 0
                    } else if (this.textAlign === 'right') {
                        lineLeftOffset = textWidth - lineWidth
                    } else {
                        // center
                        lineLeftOffset = (textWidth - lineWidth) / 2
                    }
                }
            }
            
            // ВАЖНО: Вычисляем позицию X в локальной системе координат объекта
            // 
            // В Fabric.js Text._renderTextCommon использует:
            // 1. left = this._getLeftOffset() - смещение относительно originX
            // 2. Для каждой строки добавляет lineLeftOffset = this._getLineLeftOffset(lineIndex)
            // 
            // _getLeftOffset() возвращает:
            // - При originX='left': 0
            // - При originX='center': -this.width / 2
            // - При originX='right': -this.width
            // 
            // _getLineLeftOffset() возвращает смещение строки внутри bounding box
            // на основе textAlign (не originX!):
            // - При textAlign='left': 0
            // - При textAlign='center': (textWidth - lineWidth) / 2
            // - При textAlign='right': textWidth - lineWidth
            // 
            // Итого позиция левого края строки:
            // x = _getLeftOffset() + lineLeftOffset
            // 
            // Наш код должен делать то же самое:
            let x: number
            // @ts-ignore - используем внутренний метод Fabric.js
            const leftOffset = typeof this._getLeftOffset === 'function' ? this._getLeftOffset() : 0
            x = leftOffset + lineLeftOffset
            
            // Сохраняем координату X для этой строки
            lineRenderX[i] = x
            
        }
        
        return { x: lineRenderX, widths: lineWidthsCalculated }
    }


    /**
     * Переопределяем метод _render для отключения стандартной подложки Fabric.js
     * _render вызывается из render() после применения transform
     * Временно отключаем textBackgroundColor перед вызовом родительского метода,
     * чтобы стандартная подложка не рендерилась, затем вызываем наш _renderBackground ПЕРЕД текстом
     */
    _render(ctx: CanvasRenderingContext2D): void {
        // Сохраняем оригинальный textBackgroundColor
        const originalBackgroundColor = this.textBackgroundColor
        
        // Если есть подложка, рисуем её ПЕРЕД текстом
        if (originalBackgroundColor) {
            // Временно отключаем стандартную подложку для родительского метода
            // @ts-ignore - временно отключаем стандартную подложку
            this.textBackgroundColor = undefined
            
            // Вызываем наш кастомный _renderBackground ПЕРЕД текстом
            // Контекст уже в правильном состоянии (transform применен в render())
            // @ts-ignore - восстанавливаем для нашего метода
            this.textBackgroundColor = originalBackgroundColor
            this._renderBackground(ctx)
            
            // Снова отключаем для super._render()
            // @ts-ignore - отключаем для родительского метода
            this.textBackgroundColor = undefined
        }
        
        // Вызываем родительский метод _render (он отрисует текст без стандартной подложки)
        super._render(ctx)
        
        // Восстанавливаем textBackgroundColor
        if (originalBackgroundColor) {
            // @ts-ignore - восстанавливаем стандартную подложку
            this.textBackgroundColor = originalBackgroundColor
        }
    }

    /**
     * Переопределяем метод рендеринга подложки для поддержки padding и border radius
     * Рисует подложку для каждой строки отдельно, огибая контур текста
     * Метод вызывается в локальной системе координат объекта (с учетом transform)
     * ВАЖНО: Используем те же вычисления, что и Fabric.js для рендеринга текста
     */
    _renderBackground(ctx: CanvasRenderingContext2D): void {
        // Получаем цвет подложки
        const backgroundColor = this.textBackgroundColor
        if (!backgroundColor) {
            // Если подложки нет, не рисуем ничего
            return
        }

        // Получаем padding и border radius (используем константы по умолчанию, если не заданы)
        const basePadding = this.textBackgroundPadding ?? TEXT_OBJECT_PADDING.BACKGROUND
        const borderRadius = this.textBackgroundBorderRadius ?? TEXT_OBJECT_BACKGROUND_BORDER_RADIUS
        
        // Для визуально равномерного padding увеличиваем горизонтальный padding
        // (по бокам может казаться меньше из-за особенностей рендеринга текста)
        const horizontalPadding = basePadding + 2 // Увеличиваем на 1px для визуального баланса
        const verticalPadding = basePadding // Вертикальный padding остается базовым

        // Сохраняем текущее состояние контекста
        ctx.save()

        // Получаем информацию о строках из внутренних свойств Fabric.js
        // Используем кэшированные значения, если они доступны
        const textLines = this._getTextLinesArray(this.text)
        if (textLines.length === 0) {
            ctx.restore()
            return
        }

        // Получаем ширину и высоту строк из внутренних свойств Fabric.js
        // @ts-ignore - доступ к внутренним свойствам Fabric.js
        const lineWidths: number[] | undefined = this.__lineWidths
        // @ts-ignore - доступ к внутренним свойствам Fabric.js
        const lineHeights: number[] | undefined = this.__lineHeights

        const fontSize = this.fontSize ?? 12
        const lineHeight = this.lineHeight ?? 1
        const defaultLineHeight = fontSize * lineHeight

        // Устанавливаем цвет заливки
        ctx.fillStyle = backgroundColor

        // ВАЖНО: Вычисляем координаты X и ширины для каждой строки ПЕРЕД рисованием подложки
        // Используем те же вычисления, что и Fabric.js для рендеринга текста
        const { x: lineRenderX, widths: lineWidthsCalculated } = this._calculateLineRenderX(ctx)

        // Рассчитываем начальную позицию Y для первой строки
        // В Fabric.js координаты относительно originY
        let startY: number
        const totalHeight = lineHeights
            ? lineHeights.reduce((sum, h) => sum + h, 0)
            : textLines.length * defaultLineHeight

        if (this.originY === 'top') {
            startY = -verticalPadding
        } else if (this.originY === 'bottom') {
            startY = -totalHeight - verticalPadding * 2 + verticalPadding
        } else {
            // center (по умолчанию)
            startY = -totalHeight / 2 - verticalPadding
        }

        // ВАЖНО: Рисуем все подложки как ЕДИНЫЙ PATH, чтобы избежать наложения
        // при использовании прозрачности (иначе на границах строк цвет будет темнее)
        // 
        // Алгоритм:
        // 1. Начинаем новый path
        // 2. Добавляем все прямоугольники подложек в этот path
        // 3. Заливаем path ОДИН раз
        // 
        // Это гарантирует, что даже при прозрачности подложки не будут накладываться
        
        ctx.beginPath()
        
        let currentY = startY
        for (let i = 0; i < textLines.length; i++) {
            const line = textLines[i]
            if (!line || line.trim() === '') {
                // Для пустых строк используем стандартную высоту
                currentY += defaultLineHeight
                continue
            }

            // ВАЖНО: Используем ширину строки из _calculateLineRenderX
            const lineWidth = lineWidthsCalculated[i] ?? 0

            // Получаем высоту строки из кэша или используем стандартную
            const currentLineHeight = lineHeights && lineHeights[i] !== undefined 
                ? lineHeights[i] 
                : defaultLineHeight
            
            // Позиция X подложки (с учетом horizontalPadding)
            const lineLeft = lineRenderX[i] !== undefined 
                ? lineRenderX[i] - horizontalPadding
                : -horizontalPadding

            // Размеры подложки для строки
            const bgWidth = lineWidth + horizontalPadding * 2
            const bgHeight = currentLineHeight + verticalPadding * 2

            // Добавляем прямоугольник в path (без заливки)
            if (borderRadius > 0) {
                addRoundedRectToPath(ctx, lineLeft, currentY, bgWidth, bgHeight, borderRadius)
            } else {
                addRectToPath(ctx, lineLeft, currentY, bgWidth, bgHeight)
            }

            // Переходим к следующей строке
            currentY += currentLineHeight
        }
        
        // Заливаем ВСЕ подложки ОДНИМ вызовом fill()
        // Это предотвращает наложение прозрачности на границах строк
        ctx.fill()

        // Восстанавливаем состояние контекста
        ctx.restore()
    }

    /**
     * Вспомогательный метод для разбиения текста на строки
     * Использует тот же алгоритм, что и Fabric.js
     */
    private _getTextLinesArray(text: string): string[] {
        if (!text) {
            return []
        }
        // Используем тот же разделитель, что и Fabric.js (\n для переноса строк)
        return text.split('\n')
    }
}
